import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useMemo } from "react";
import imageCache from "sprout-lib/dist/image-cache";
import { Box, Flex, Text } from "sprout-ui-primitives";
import { media } from "sprout-ui-theme";
import styled, { css } from "styled-components";
import { Image } from "sprout-ui-dom/dist/Image";
import BaseUrlLink from "../UrlLink";
import isSproutSupportedVideo from "./isSproutSupportedVideo";
import UrlDisplayVideo from "./UrlDisplayVideo";
import UrlDisplayTwitter from "./UrlDisplayTwitter";
import isTwitter from "./isTwitter";
const UrlDisplayWrapper = styled(Flex)`
  display: flex;

  ${media.up("medium", "pointer")} {
    transition: background 0.15s linear;
    :hover {
      /* background: #c5d2e0; */
      background: #e9f0f7;
    }
  }

  ${({ $compact  })=>$compact && css`
      display: inline-flex;
    `}
`;
const UrlLink = styled(BaseUrlLink)`
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
`;
const UrlDisplayContentWrapper = styled(Box)`
  padding: 6px 16px 6px 8px;

  ${({ $compact  })=>!$compact && css`
      padding: 8px;

      ${media.up("small")} {
        padding: 8px 16px;
      }
      ${media.up("medium")} {
        padding: 16px;
      }
    `}
`;
const LineClamp = styled(Text).attrs({
    as: "span"
})`
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow-wrap: break-word;
  word-wrap: break-word;
  word-break: break-word;
  ${"" /* overflow-wrap: break-word; */ };
  ${"" /* word-wrap is alias to overflow-wrap */ };
  ${"" /* word-wrap: break-word */ };

  line-height: 20px;
  /* line-clamp * line-height */
  max-height: 40px;
`;
const Title = styled(LineClamp)`
  font-size: 14px;

  ${({ $compact  })=>!$compact && css`
      ${media.up("small")} {
        font-size: 15px;
      }

      ${media.up("medium")} {
        font-size: 17px;
      }
    `}
`;
const Description = styled(LineClamp)`
  font-size: 13px;

  ${({ $compact  })=>!$compact && css`
      ${media.down("small")} {
        -webkit-line-clamp: 1;
      }

      ${media.up("small")} {
        font-size: 14px;
      }

      ${media.up("medium")} {
        font-size: 15px;
      }
    `}
`;
const Info = styled(Text).attrs({
    as: "span"
})`
  /* display: inline-block; */
`;
const ImageCentered = styled(Image)`
  object-fit: cover;
  object-position: center;
`;
const UrlImageWrapper = styled(Flex)`
  width: 58px;
  position: relative;

  ${({ $compact  })=>!$compact && css`
      width: 100px;
      height: 100px;

      ${media.up("small")} {
        width: 140px;
        height: 140px;
      }

      ${media.up("medium")} {
        width: 160px;
        height: 160px;
      }
    `}

  ${ImageCentered} {
    position: absolute;
    height: 100%;
    width: 100%;
  }
`;
const LogoWrapper = styled(Flex)`
  width: 20px;
  height: 20px;
  border-radius: 50%;
  overflow: hidden;
  flex-shrink: 0;
`;
const UrlDisplay = (props)=>{
    const { url , title , description , compact , websiteName , logo , image  } = props;
    const info = useMemo(()=>{
        if (websiteName) return websiteName;
        try {
            const { hostname  } = new URL(url);
            return hostname;
        } catch (e) {
            return url;
        }
    }, [
        url,
        websiteName
    ]);
    const videoSrc = compact ? undefined : isSproutSupportedVideo(url);
    if (videoSrc) return /*#__PURE__*/ _jsx(UrlDisplayVideo, {
        src: videoSrc,
        title: title || description || undefined
    });
    const tweetId = compact ? undefined : isTwitter(url);
    if (tweetId) return /*#__PURE__*/ _jsx(UrlDisplayTwitter, {
        tweetId: tweetId
    });
    return /*#__PURE__*/ _jsxs(UrlDisplayWrapper, {
        position: "relative",
        alignItems: "center",
        // bg="blue.50"
        border: "1px solid rgba(0,0,0,.12)",
        borderRadius: "14px",
        $compact: compact,
        children: [
            /*#__PURE__*/ _jsx(UrlLink, {
                url: url
            }),
            /*#__PURE__*/ _jsxs(UrlDisplayContentWrapper, {
                flex: "1",
                minWidth: "0",
                $compact: compact,
                children: [
                    !compact && /*#__PURE__*/ _jsxs(Flex, {
                        alignItems: "center",
                        mb: [
                            "2px",
                            1
                        ],
                        children: [
                            logo && /*#__PURE__*/ _jsx(LogoWrapper, {
                                mr: 1,
                                children: /*#__PURE__*/ _jsx(ImageCentered, {
                                    src: imageCache(logo, {
                                        w: 80
                                    })
                                })
                            }),
                            /*#__PURE__*/ _jsx(Info, {
                                truncate: true,
                                color: "black.40",
                                fontSize: "13px",
                                children: info
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsx(Title, {
                        fontWeight: "bold",
                        $compact: compact,
                        children: info.toLowerCase().includes("twitter") ? description || title || url : title || url
                    }),
                    !compact && description && /*#__PURE__*/ _jsx(Description, {
                        mt: [
                            "2px",
                            1
                        ],
                        $compact: compact,
                        children: description
                    })
                ]
            }),
            image && /*#__PURE__*/ _jsxs(UrlImageWrapper, {
                position: "relative",
                alignSelf: "stretch",
                borderRadius: "inherit",
                $compact: compact,
                children: [
                    /*#__PURE__*/ _jsx(ImageCentered, {
                        src: imageCache(image, {
                            w: compact ? 320 : 600
                        })
                    }),
                    compact && logo && /*#__PURE__*/ _jsx(LogoWrapper, {
                        position: "absolute",
                        bottom: "-10px",
                        left: "-10px",
                        children: /*#__PURE__*/ _jsx(ImageCentered, {
                            src: imageCache(logo, {
                                w: 80
                            })
                        })
                    })
                ]
            })
        ]
    });
};
export default UrlDisplay;
